import { useMemo, useState, useEffect, useCallback } from 'react';
import usePrevious from 'use-previous';

import { isMsBrowser } from 'app/shared/utils/utils';
import { LEADS_BENCHMARK_TRACKING } from 'app/dealer/dashboard/Dashboard/LeadsBenchmark/tracking/index';
import { LISTINGS_BENCHMARK_TRACKING } from 'app/dealer/dashboard/Dashboard/ListingsBenchmark/tracking/index';

import { DEALERS_INSIGHTS_FEATURES } from '../modules/dealersInsights/api/dealersInsights.api';
import { PICKER_TRACKING_ACTIONS } from '../components/DummyPicker/DummyPicker';

import { useKeyPress } from './useKeyPress';

// TODO: consider replacing this hook with this refactor:
// https://github.com/hey-car/dealer-portal/pull/6
// branch also includes `react-hooks-testing-library
// note: add keyboard controls to the PR above
// this hook is very difficult to test in its current state, and could be broken
// down into smaller parts

/**
 * @typedef UsePickerApi
 * @property {number} currentMonth
 * @property {number} currentYear
 * @property {function} increment
 * @property {function} decrement
 * @property {{value: string, year: 'numeric', month?: 'long'}} intlProps
 * @property {function} memoCannotGoBack
 * @property {function} memoCannotGoForward
 */

/**
 * @typedef {Object} UsePickerParams
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} selectedDate
 * @property {'month'|'year'} timeDivision
 * @property {boolean} pickerInView - true if picker is visible in viewport
 * @property {import('../../account/Network/reducer').Network} network
 * @property {string} feature
 */

/**
 *
 * @param {Object} param0
 * @returns {{currentMonth: number, currentYear: number, increment: () => void, decrement: () => void, intlProps, memoCannotGoBack, memoCannotGoForward}}
 */
export const usePicker = ({
  startDate,
  endDate,
  selectedDate,
  timeDivision,
  pickerInView,
  network,
  feature,
}) => {
  // check for invalid date ranges

  if (new Date(startDate) > new Date(endDate)) {
    /* istanbul ignore next */
    throw new Error(
      `usePicker startDate must come before endDate. Given startDate is ${startDate}. Given endDate is ${endDate}`
    );
  }
  // state for month
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selectedDate).getMonth()
  );

  // state for year
  const [currentYear, setCurrentYear] = useState(
    new Date(selectedDate).getFullYear()
  );

  const prevProps = usePrevious({ startDate });

  useEffect(() => {
    // if we receive a new start date
    // from the date range api
    // we need to update the current date in the picker
    // this will happen when we filter a new dealer level
    // that has a different data date range
    if (prevProps?.startDate !== startDate) {
      setCurrentMonth(new Date(selectedDate).getMonth());
      setCurrentYear(new Date(selectedDate).getFullYear());
    }
  }, [selectedDate, startDate, prevProps]);

  // memoize start month
  const memoStartMonth = useMemo(() => new Date(startDate).getMonth(), [
    startDate,
  ]);

  // memoize start year
  const memoStartYear = useMemo(() => new Date(startDate).getFullYear(), [
    startDate,
  ]);

  // memoize end month
  const memoEndMonth = useMemo(() => new Date(endDate).getMonth(), [endDate]);

  // memoize end year
  const memoEndYear = useMemo(() => new Date(endDate).getFullYear(), [endDate]);

  const formattedIntlMonth =
    memoEndMonth < 10 ? `0${memoEndMonth}` : memoEndMonth;

  // state for intlProps
  const [intlProps, setIntlProps] = useState({
    value: `${memoEndYear}-${formattedIntlMonth}`,
    year: 'numeric',
    month: 'long',
  });

  // memoize cannot go back bool
  const memoCannotGoBack = useMemo(() => {
    if (timeDivision === 'year') {
      return currentYear <= memoStartYear;
    }
    return memoStartMonth === currentMonth && memoStartYear === currentYear;
  }, [currentMonth, currentYear, memoStartMonth, memoStartYear, timeDivision]);

  // memoize cannot go forward bool
  const memoCannotGoForward = useMemo(() => {
    if (timeDivision === 'year') {
      return currentYear >= memoEndYear;
    }
    return currentMonth >= memoEndMonth && currentYear === memoEndYear;
  }, [currentMonth, currentYear, memoEndMonth, memoEndYear, timeDivision]);

  /**
   * main effect
   * @description
   * set current month to end month if current exceeds end
   * set current year to end year if current exceeds end
   * set intlProps
   */
  useEffect(() => {
    /* istanbul ignore next */ if (
      currentMonth < memoStartMonth &&
      currentYear === memoStartYear
    ) {
      setCurrentMonth(memoStartMonth);
    }
    /* istanbul ignore next */ if (
      currentMonth > memoEndMonth &&
      currentYear === memoEndYear
    ) {
      setCurrentMonth(memoEndMonth);
    }
    let intlMonth = currentMonth + 1;
    if (intlMonth < 10) {
      intlMonth = `0${intlMonth}`;
    }
    let intlObj = {
      value: `${currentYear}-${intlMonth}`,
      year: 'numeric',
    };
    if (timeDivision === 'year') {
      intlObj = { ...intlObj };
    } else {
      intlObj = { ...intlObj, month: 'long' };
    }
    setIntlProps(intlObj);
  }, [
    timeDivision,
    setCurrentMonth,
    setCurrentYear,
    currentMonth,
    currentYear,
    memoStartMonth,
    memoStartYear,
    memoEndMonth,
    memoEndYear,
  ]);

  // increment date
  const increment = useCallback(
    ({ action }) => {
      if (memoCannotGoForward) {
        return;
      }
      if (timeDivision === 'year') {
        setCurrentYear(currentYear + 1);
      } else {
        if (currentMonth === 11) {
          setCurrentYear(currentYear + 1);
        }
        setCurrentMonth((currentMonth + 1) % 12);
      }

      // tracking
      /* istanbul ignore next */ if (network) {
        if (feature === DEALERS_INSIGHTS_FEATURES.LEADS) {
          // TODO: fix the timeDivision naming
          LEADS_BENCHMARK_TRACKING.trackDatePicker({
            network,
            action,
            selectedTimeDivisionFilter: timeDivision,
          });
        }
        if (feature === DEALERS_INSIGHTS_FEATURES.LISTINGS) {
          // TODO: fix the timeDivision naming
          LISTINGS_BENCHMARK_TRACKING.trackDatePicker({
            network,
            action,
            selectedTimeDivisionFilter: timeDivision,
          });
        }
      }
    },
    [
      currentMonth,
      currentYear,
      feature,
      memoCannotGoForward,
      network,
      timeDivision,
    ]
  );

  // decrement date
  const decrement = useCallback(
    ({ action }) => {
      if (memoCannotGoBack) {
        return;
      }
      if (timeDivision === 'year') {
        setCurrentYear(currentYear - 1);
      } else if (currentMonth === 0) {
        setCurrentYear(currentYear - 1);
        setCurrentMonth(11);
      } else {
        setCurrentMonth(currentMonth - 1);
      }

      // tracking
      /* istanbul ignore next */ if (network) {
        if (feature === DEALERS_INSIGHTS_FEATURES.LEADS) {
          // TODO: fix the timeDivision naming
          LEADS_BENCHMARK_TRACKING.trackDatePicker({
            network,
            action,
            selectedTimeDivisionFilter: timeDivision,
          });
        }
        if (feature === DEALERS_INSIGHTS_FEATURES.LISTINGS) {
          // TODO: fix the timeDivision naming
          LISTINGS_BENCHMARK_TRACKING.trackDatePicker({
            network,
            action,
            selectedTimeDivisionFilter: timeDivision,
          });
        }
      }
    },
    [
      currentMonth,
      currentYear,
      feature,
      memoCannotGoBack,
      network,
      timeDivision,
    ]
  );

  /**
   * @description
   * keyboard controls
   *
   * */

  // keyboard controls
  const arrowLeftKey = isMsBrowser() ? 'Left' : 'ArrowLeft';
  const arrowRightKey = isMsBrowser() ? 'Right' : 'ArrowRight';

  const keyLeftToPrevious = useKeyPress(arrowLeftKey);
  const keyRightToNext = useKeyPress(arrowRightKey);

  // when arrow left is pressed
  // if date picker is in the viewport
  // and the picker has not reached the start date
  // decrement to the previous date
  useEffect(() => {
    if (pickerInView && !memoCannotGoBack && keyLeftToPrevious) {
      decrement({
        action: PICKER_TRACKING_ACTIONS.DECREMENT_WITH_KEYBOARD,
      });
    }
    // eslint-disable-next-line
  }, [pickerInView, memoCannotGoBack, keyLeftToPrevious]);

  // when arrow right is pressed
  // if date picker is in the viewport
  // and the picker has not bypassed the end dat
  // increment to the next date
  useEffect(() => {
    if (pickerInView && !memoCannotGoForward && keyRightToNext) {
      increment({
        action: PICKER_TRACKING_ACTIONS.INCREMENT_WITH_KEYBOARD,
      });
    }
    // eslint-disable-next-line
  }, [pickerInView, memoCannotGoForward, keyRightToNext]);

  // return hook api
  return {
    currentMonth,
    currentYear,
    increment,
    decrement,
    intlProps,
    memoCannotGoBack,
    memoCannotGoForward,
  };
};
